import React, { useEffect, useRef } from 'react'
import { css } from '@emotion/react'
import { mq, Tab } from 'plume-ui'
import { useUser } from '../../../contexts/AuthContext'
import { investorTabLabel, isCvc, isVc } from '@/lib/investor'
import { isEnterpriseWithTrial } from '@/lib/user'
import {
  CvcOnlyTabType,
  InvestorDataCount,
  InvestorSummary,
  InvestorTabType,
  VCOnlyTabType,
} from '../../../types'
import { Navbar } from '../../common/Navbar'
import { throttle } from 'lodash'

export const allTabList: InvestorTabType[] = [
  'basic-info',
  'investment-info',
  'companies',
  'seed-funded',
  'acquisitions',
  'partnerships',
  'people',
  'funds',
  'startup-investments',
  'lp-investments',
  'news',
]

const vcOnlyTab: VCOnlyTabType[] = ['investment-info', 'seed-funded']
const cvcOnlyTab: CvcOnlyTabType[] = ['acquisitions', 'partnerships']

const enterpriseOnlyTab: InvestorTabType[] = [
  'funds',
  'startup-investments',
  'lp-investments',
]

type Props = {
  summary: InvestorSummary
  activeTab: InvestorTabType | null
  onChangeTab?: Function
}

export const InvestorTabGroup: React.FC<Props> = (props) => {
  const user = useUser()

  const ref = useRef<HTMLDivElement>(null)

  const check = () => {
    if (ref.current === null) return
    const activeButton =
      ref.current.querySelector<HTMLElement>('.pl-tab.is-active')
    if (activeButton === null) return
    ref.current.scroll({
      left: activeButton.offsetLeft - 32,
      behavior: 'smooth',
    })
  }

  useEffect(() => {
    check()
  }, [props.activeTab])

  useEffect(() => {
    window.addEventListener('scroll', throttle(check, 200))
    return () => {
      window.removeEventListener('scroll', throttle(check, 200))
    }
  }, [])

  return (
    <Navbar
      css={css`
        padding: 7px 0;
        height: 48px;
        ${mq.large} {
          height: auto;
          padding: 11px 0;
          position: sticky;
          top: 160px;
          z-index: 19;
        }
      `}
      onClick={props.onChangeTab && props.onChangeTab(props.activeTab)}
    >
      <div
        ref={ref}
        css={css`
          ${mq.untilMedium} {
            position: sticky;
            overflow-x: auto;
            scrollbar-width: none;
            -ms-overflow-style: none;
            &::-webkit-scrollbar {
              display: none;
            }
          }
        `}
      >
        <Tab.Group
          css={css`
            max-width: 1200px;
            margin: 0 auto;
            ${mq.untilMedium} {
              width: min-content;
              padding: 0 16px;
            }
          `}
        >
          {allTabList.map((tab, i) => {
            if (!isVc(props.summary) && (vcOnlyTab as string[]).includes(tab)) {
              return null
            }

            if (
              !isCvc(props.summary) &&
              (cvcOnlyTab as string[]).includes(tab)
            ) {
              return null
            }

            if (
              !isEnterpriseWithTrial(user) &&
              enterpriseOnlyTab.includes(tab)
            ) {
              return null
            }

            return (
              <Tab
                css={css`
                  ${mq.untilMedium} {
                    font-size: 14px;
                    height: 32px;
                  }
                `}
                key={i}
                active={props.activeTab === tab}
                as="a"
                href={`/investors/${props.summary.shortname}#${tab}`}
                count={countOfTab(tab, props.summary.dataCount)}
              >
                {investorTabLabel[tab]}
              </Tab>
            )
          })}
        </Tab.Group>
      </div>
    </Navbar>
  )
}

const countOfTab = (
  tabType: InvestorTabType,
  dataCount: InvestorDataCount
): number | undefined => {
  switch (tabType) {
    case 'companies':
      return dataCount.companies
    case 'funds':
      return dataCount.funds
    case 'startup-investments':
      return dataCount.investments
    case 'lp-investments':
      return dataCount.lp
    case 'seed-funded':
      return dataCount.seeds
    case 'acquisitions':
      return dataCount.acquisitions
    case 'partnerships':
      return dataCount.partnerships
    default:
      return undefined
  }
}
